/**
 * AgentSidebar — the persistent right-hand chat panel (Manus-style
 * chat-beside-the-work). Fills its column and collapses to a slim rail.
 *
 * Renders an AppState (see app-state.ts) as a scrollable conversation:
 * user / agent bubbles (the agent voice uses the display serif sparingly for
 * its reply body), a live streaming caret on the in-flight agent turn,
 * tool-call chips (lucide glyph + name, ok / bad / pending), an error banner,
 * and a fixed composer (Textarea + Send) pinned to the bottom.
 *
 * Layout contract: the root is a flex column — a fixed header, a `flex:1;
 * min-height:0; overflow:auto` thread region, and a fixed composer that never
 * gets pushed off. The panel owns its own *height* only; its *width* is owned
 * by the parent grid track, which animates the collapse separately. When
 * `collapsed`, the root exposes `data-collapsed="true"` so the grid can key its
 * track width off a single source of truth.
 *
 * Pure presentation: every datum arrives via props; the container owns the
 * client, the AppState reducer, and the input string. Colours come only from
 * design tokens (var(--…)) and the cream `ui` primitives — no hex, no fetch.
 * All user-facing copy routes through `useTr()` (zh / en).
 */

import { useEffect, useRef, type KeyboardEvent } from "react";
import {
  Sparkles,
  Send,
  PanelRightClose,
  PanelRightOpen,
  Check,
  X,
  Loader2,
  Wrench,
  AlertTriangle,
  Gauge,
  Undo2,
  Play,
  GitBranch,
  PencilLine,
} from "lucide-react";
import { Button } from "../../ui/Button.js";
import { IconButton } from "../../ui/IconButton.js";
import { Textarea } from "../../ui/Textarea.js";
import { ScrollArea } from "../../ui/ScrollArea.js";
import { EmptyState } from "../../ui/EmptyState.js";
import { useTr, type Entry } from "../../i18n/context.js";
import type { AppState, ChatTurn, ToolActivity, QualityScore } from "../../app-state.js";
import { AgentConfirmationCard, type AgentConfirmationAnswer, type AgentConfirmationBrief } from "./AgentConfirmation.js";
import "./AgentSidebar.css";

/** Local bilingual copy — shared strings pulled in via COMMON where they exist. */
const S = {
  title: { zh: "AI 助手", en: "AI Assistant" },
  ariaPanel: { zh: "AI 助手", en: "AI assistant" },
  collapse: { zh: "收起 AI 助手", en: "Collapse the AI assistant" },
  expand: { zh: "展开 AI 助手", en: "Expand the AI assistant" },

  emptyTitle: { zh: "开始与 AI 共创", en: "Start creating with AI" },
  emptyDesc: {
    zh: "描述你想要的剧情、分支或场景，让助手帮你落笔。",
    en: "Describe the plot, branch, or scene you want — the assistant helps you write it.",
  },

  thinking: { zh: "正在思考", en: "Thinking" },

  // Prominent "AI is working" banner (pinned above the composer while running).
  working: { zh: "AI 正在创作…", en: "AI is working…" },
  workingSub: { zh: "请稍候，完成后可继续补充需求", en: "Hang tight — add follow-up notes when this finishes" },
  busyList: { zh: "正在查看剧集…", en: "Browsing the episodes…" },
  busyRead: { zh: "正在读取剧本…", en: "Reading the script…" },
  busyWrite: { zh: "正在撰写剧本…", en: "Writing the script…" },
  busyCompile: { zh: "正在编译校验…", en: "Compiling & checking…" },
  busyAssets: { zh: "正在查看素材…", en: "Checking the assets…" },
  busyReadPlan: { zh: "正在读取故事大纲…", en: "Reading the story plan…" },
  busyWritePlan: { zh: "正在规划故事大纲…", en: "Planning the story…" },
  busyConfirm: { zh: "正在整理确认问题…", en: "Preparing confirmation…" },
  busyScore: { zh: "正在自评质量…", en: "Self-scoring quality…" },

  toolsLabel: { zh: "工具调用", en: "Tool calls" },
  toolPending: { zh: "进行中", en: "In progress" },
  toolOk: { zh: "成功", en: "Succeeded" },
  toolBad: { zh: "失败", en: "Failed" },
  toolUnknown: { zh: "处理请求", en: "Working" },
  toolUnsupported: { zh: "不支持的操作", en: "Unsupported action" },
  errNetwork: { zh: "网络暂时不可用，请稍后重试。", en: "The network is temporarily unavailable. Please try again." },
  errBudget: { zh: "这次任务太长了，请拆成更小的一步再试。", en: "This request was too large. Try a smaller step." },

  // Quality self-score card (the agent's end-of-turn self-assessment).
  qualityTitle: { zh: "AI 质量自评", en: "AI quality self-check" },
  qualityHint: {
    zh: "这是 AI 对本次剧本的自评，供你参考与手动修改。",
    en: "The AI's own take on this draft — a guide for your manual revisions.",
  },
  qualityStrengths: { zh: "亮点", en: "Strengths" },
  qualityImprovements: { zh: "可以改进", en: "To improve" },
  undoTitle: { zh: "本次 AI 修改可撤销", en: "This AI change can be undone" },
  undoBody: {
    zh: "恢复到发送这条指令前的剧集内容，也可以按 Ctrl/⌘ + Z。",
    en: "Restore the episodes to the state before this prompt. You can also press Ctrl/⌘ + Z.",
  },
  undoAction: { zh: "撤销本次修改", en: "Undo this change" },
  undoing: { zh: "正在撤销", en: "Undoing" },
  resultTitle: { zh: "剧本已更新", en: "Draft updated" },
  resultBody: {
    zh: "下一步可以直接预览成品、查看分支结构，或回到文本继续细调。",
    en: "Next, preview the playable scene, inspect the branches, or refine the text.",
  },
  resultChanged: { zh: "已更新", en: "Updated" },
  resultValidationOk: { zh: "写后校验通过", en: "Post-write check passed" },
  resultValidationIssues: { zh: "写后校验发现", en: "Post-write check found" },
  resultValidationIssueUnit: { zh: "个问题", en: "issues" },
  resultPreview: { zh: "预览成品", en: "Preview" },
  resultCanvas: { zh: "看结构", en: "Canvas" },
  resultEditor: { zh: "继续编辑", en: "Edit" },
  contextLabel: { zh: "将随指令发送", en: "Sent with prompt" },

  composerLabel: { zh: "给 AI 的指令", en: "Message for the AI" },
  placeholder: {
    zh: "让助手帮你写一段剧情…（⌘↵ 发送）",
    en: "Ask the assistant to write a scene… (⌘↵ to send)",
  },
  pendingPlaceholder: {
    zh: "补充这次确认的说明…（⌘↵ 确认并继续）",
    en: "Add a note for this confirmation… (⌘↵ to confirm)",
  },
  pendingNote: {
    zh: "这里会作为补充说明随确认一起发送；也可以直接点确认继续。",
    en: "This will be sent as a note with your confirmation. You can also confirm directly.",
  },
  confirmContinue: { zh: "确认并继续", en: "Confirm & continue" },
  send: { zh: "发送", en: "Send" },
} satisfies Record<string, Entry>;

export interface AgentContextChip {
  readonly label: string;
  readonly value: string;
  readonly tone?: "neutral" | "warning" | "danger";
}

export interface AgentResultIssue {
  readonly sceneId: string;
  readonly diagnostics: readonly string[];
}

/** pending → spinner, ok → check, bad → cross. */
function toolStateOf(tool: ToolActivity): "pending" | "ok" | "bad" {
  if (tool.ok === undefined) return "pending";
  return tool.ok ? "ok" : "bad";
}

/** Friendly current-activity label from the in-flight tool (falls back to a
 *  generic "AI is working"). Keeps the running state legible ("知道在干嘛"). */
const BUSY_LABELS: Record<string, Entry> = {
  list_scenes: S.busyList,
  read_scene: S.busyRead,
  write_scene: S.busyWrite,
  compile_scene: S.busyCompile,
  list_assets: S.busyAssets,
  read_plan: S.busyReadPlan,
  write_plan: S.busyWritePlan,
  request_confirmation: S.busyConfirm,
  report_quality: S.busyScore,
};

const TOOL_LABELS: Record<string, Entry> = {
  list_scenes: { zh: "查看剧集", en: "Browsing episodes" },
  read_scene: { zh: "读取剧本", en: "Reading script" },
  write_scene: { zh: "写入剧集", en: "Writing episode" },
  compile_scene: { zh: "检查可玩性", en: "Checking playability" },
  list_assets: { zh: "查看素材", en: "Checking assets" },
  read_plan: { zh: "读取大纲", en: "Reading outline" },
  write_plan: { zh: "保存大纲", en: "Saving outline" },
  read_skill: { zh: "学习写作规则", en: "Loading writing rules" },
  request_confirmation: { zh: "确认方向", en: "Confirming direction" },
  report_quality: { zh: "质量自评", en: "Self-checking quality" },
};

function toolLabel(tool: ToolActivity, tr: (e: Entry) => string): string {
  if (tool.detail?.includes("unknown tool")) return tr(S.toolUnsupported);
  return tr(TOOL_LABELS[tool.name] ?? S.toolUnknown);
}

function friendlyError(message: string, tr: (e: Entry) => string): string {
  const lower = message.toLowerCase();
  if (lower.includes("gateway") || lower.includes("fetch") || lower.includes("network") || lower.includes("http 5")) {
    return tr(S.errNetwork);
  }
  if (lower.includes("step budget") || lower.includes("budget")) {
    return tr(S.errBudget);
  }
  return message;
}

/** Tier a 0–100 self-score into a colour band for the card badge. */
function scoreTier(score: number): "high" | "mid" | "low" {
  if (score >= 80) return "high";
  if (score >= 60) return "mid";
  return "low";
}

/** The agent's end-of-turn quality self-score, shown for the user to act on. */
function QualityCard({ quality }: { readonly quality: QualityScore }): JSX.Element {
  const tr = useTr();
  const score = Math.max(0, Math.min(100, Math.round(quality.score)));
  return (
    <div className="lw-agent__quality" data-tier={scoreTier(score)} role="note" aria-label={tr(S.qualityTitle)}>
      <div className="lw-agent__quality-head">
        <Gauge size={15} className="lw-agent__quality-icon" aria-hidden="true" />
        <span className="lw-agent__quality-title">{tr(S.qualityTitle)}</span>
        <span className="lw-agent__quality-score">
          {score}
          <span className="lw-agent__quality-max">/100</span>
        </span>
      </div>
      {quality.strengths ? (
        <p className="lw-agent__quality-line">
          <span className="lw-agent__quality-label">{tr(S.qualityStrengths)}</span>
          {quality.strengths}
        </p>
      ) : null}
      {quality.improvements && quality.improvements.length > 0 ? (
        <div className="lw-agent__quality-line">
          <span className="lw-agent__quality-label">{tr(S.qualityImprovements)}</span>
          <ul className="lw-agent__quality-list">
            {quality.improvements.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="lw-agent__quality-hint">{tr(S.qualityHint)}</p>
    </div>
  );
}

function ResultActions({
  changedSceneIds,
  validationChecked,
  validationIssues = [],
  onOpenPreview,
  onOpenCanvas,
  onOpenEditor,
}: {
  readonly changedSceneIds: readonly string[];
  readonly validationChecked?: boolean;
  readonly validationIssues?: readonly AgentResultIssue[];
  readonly onOpenPreview?: () => void;
  readonly onOpenCanvas?: () => void;
  readonly onOpenEditor?: () => void;
}): JSX.Element {
  const tr = useTr();
  const changed = changedSceneIds.join("、");
  const visibleIssues = validationIssues.slice(0, 3);
  return (
    <div className="lw-agent__result" role="note">
      <div className="lw-agent__result-copy">
        <span className="lw-agent__result-title">{tr(S.resultTitle)}</span>
        <span className="lw-agent__result-meta" title={changed}>
          {tr(S.resultChanged)}：{changed}
        </span>
        <span className="lw-agent__result-body">{tr(S.resultBody)}</span>
      </div>
      {validationChecked ? (
        <div
          className="lw-agent__result-validation"
          data-state={validationIssues.length > 0 ? "issue" : "ok"}
          role={validationIssues.length > 0 ? "alert" : "status"}
        >
          <span className="lw-agent__result-validation-title">
            {validationIssues.length > 0 ? (
              <AlertTriangle size={14} aria-hidden="true" />
            ) : (
              <Check size={14} aria-hidden="true" />
            )}
            {validationIssues.length > 0
              ? `${tr(S.resultValidationIssues)} ${validationIssues.length} ${tr(S.resultValidationIssueUnit)}`
              : tr(S.resultValidationOk)}
          </span>
          {visibleIssues.length > 0 ? (
            <ul className="lw-agent__result-validation-list">
              {visibleIssues.map((issue, index) => (
                <li key={`${issue.sceneId}-${index}`}>
                  <span className="lw-agent__result-validation-scene">{issue.sceneId}</span>
                  <span>{issue.diagnostics[0] ?? "Unknown compile issue"}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="lw-agent__result-actions">
        {onOpenPreview ? (
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Play size={14} aria-hidden="true" />}
            onClick={onOpenPreview}
          >
            {tr(S.resultPreview)}
          </Button>
        ) : null}
        {onOpenCanvas ? (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<GitBranch size={14} aria-hidden="true" />}
            onClick={onOpenCanvas}
          >
            {tr(S.resultCanvas)}
          </Button>
        ) : null}
        {onOpenEditor ? (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<PencilLine size={14} aria-hidden="true" />}
            onClick={onOpenEditor}
          >
            {tr(S.resultEditor)}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function activityLabel(state: AppState, tr: (e: Entry) => string): string {
  for (let i = state.tools.length - 1; i >= 0; i--) {
    const tool = state.tools[i]!;
    if (tool.ok === undefined) return tr(BUSY_LABELS[tool.name] ?? S.working);
  }
  return tr(S.working);
}

function ToolChip({ tool }: { readonly tool: ToolActivity }): JSX.Element {
  const tr = useTr();
  const state = toolStateOf(tool);
  const icon =
    state === "pending" ? (
      <Loader2 size={13} className="lw-agent-chip__spin" aria-hidden="true" />
    ) : state === "ok" ? (
      <Check size={13} aria-hidden="true" />
    ) : (
      <X size={13} aria-hidden="true" />
    );
  const stateLabel =
    state === "pending" ? tr(S.toolPending) : state === "ok" ? tr(S.toolOk) : tr(S.toolBad);
  const name = toolLabel(tool, tr);
  return (
    <span
      className="lw-agent-chip"
      data-state={state}
      title={tool.detail ? `${tool.name}: ${tool.detail}` : tool.name}
    >
      <Wrench size={12} className="lw-agent-chip__tool" aria-hidden="true" />
      <span className="lw-agent-chip__name">{name}</span>
      <span className="lw-agent-chip__status" aria-label={stateLabel}>
        {icon}
      </span>
    </span>
  );
}

function Bubble({
  turn,
  streaming,
}: {
  readonly turn: ChatTurn;
  /** True only for the in-flight agent turn — shows the streaming caret. */
  readonly streaming: boolean;
}): JSX.Element {
  const tr = useTr();
  const isAgent = turn.role === "agent";
  return (
    <div className="lw-agent-row" data-role={turn.role}>
      <div className="lw-agent-bubble" data-role={turn.role}>
        {turn.text ? (
          <p className="lw-agent-bubble__text">{turn.text}</p>
        ) : isAgent && streaming ? (
          <p className="lw-agent-bubble__text lw-agent-bubble__text--thinking">{tr(S.thinking)}</p>
        ) : null}
        {isAgent && streaming ? <span className="lw-agent-caret" aria-hidden="true" /> : null}
      </div>
    </div>
  );
}

export interface AgentSidebarProps {
  /** The folded chat view-model (turns / tools / status / error). */
  readonly state: AppState;
  /** Current composer value (controlled by the container). */
  readonly input: string;
  /** Fired on every composer keystroke. */
  readonly onInput: (value: string) => void;
  /** Fired when the user submits the prompt (Send button / ⌘↵). */
  readonly onSend: () => void;
  /** When true, the panel collapses to a slim rail. @default false */
  readonly collapsed?: boolean;
  /** Toggles `collapsed`; when omitted the collapse control is hidden. */
  readonly onToggleCollapse?: () => void;
  /** When present, disables the composer and explains why. */
  readonly disabledReason?: string | null;
  /** Locks the composer during container-side save/compile preflight. */
  readonly busy?: boolean;
  /** Compact current-work context that will be included in the next agent run. */
  readonly contextChips?: readonly AgentContextChip[];
  /** Show a one-click rollback affordance for the latest AI write run. */
  readonly undoAvailable?: boolean;
  /** True while the latest AI write rollback is running. */
  readonly undoBusy?: boolean;
  /** Restore the latest AI write run. */
  readonly onUndo?: () => void;
  /** Scene ids written by the latest agent run. Required to show result actions. */
  readonly resultChangedSceneIds?: readonly string[];
  /** True when the changed scenes were compiled after the run finished. */
  readonly resultValidationChecked?: boolean;
  /** Compile diagnostics found by the post-run check. */
  readonly resultValidationIssues?: readonly AgentResultIssue[];
  /** Local, pre-run confirmation. Confirming is the first action that starts the SSE agent. */
  readonly pendingConfirmation?: AgentConfirmationBrief | null;
  readonly onConfirmPending?: (answers?: readonly AgentConfirmationAnswer[]) => void;
  readonly onRevisePending?: () => void;
  /** Compile and open the current episode in the preview surface. */
  readonly onOpenPreview?: () => void;
  /** Open the canvas surface to inspect the generated structure. */
  readonly onOpenCanvas?: () => void;
  /** Open the text editor surface for precise edits. */
  readonly onOpenEditor?: () => void;
}

export function AgentSidebar({
  state,
  input,
  onInput,
  onSend,
  collapsed = false,
  onToggleCollapse,
  disabledReason,
  busy = false,
  contextChips = [],
  undoAvailable,
  undoBusy,
  onUndo,
  resultChangedSceneIds,
  resultValidationChecked,
  resultValidationIssues,
  pendingConfirmation,
  onConfirmPending,
  onRevisePending,
  onOpenPreview,
  onOpenCanvas,
  onOpenEditor,
}: AgentSidebarProps): JSX.Element {
  const tr = useTr();
  const running = state.status === "running" || busy;
  const hasTurns = state.turns.length > 0;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Pin to the latest message as the agent streams. When a confirmation brief
  // appears, keep its heading in view instead of dumping the user at the card tail.
  useEffect(() => {
    const viewport = scrollRef.current?.querySelector<HTMLElement>(".lc-scroll__viewport");
    if (!viewport) return;
    const confirmation = viewport.querySelector<HTMLElement>(".lw-agent-confirm");
    if (pendingConfirmation && confirmation) {
      confirmation.scrollIntoView({ block: "start" });
      return;
    }
    viewport.scrollTop = viewport.scrollHeight;
  }, [pendingConfirmation, state.turns, state.tools, state.status]);

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    // ⌘↵ / Ctrl+↵ submits; plain ↵ keeps inserting newlines.
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (pendingConfirmation && onConfirmPending && !running && !disabledReason) onConfirmPending();
      else if (input.trim() && !running && !disabledReason) onSend();
    }
  }

  if (collapsed) {
    // Slim rail: an expand affordance (a right-pointing panel-open glyph) over a
    // calm sparkle mark. Nothing else — the thread + composer stay unmounted.
    return (
      <aside
        className="lw-agent lw-agent--collapsed"
        data-collapsed="true"
        aria-label={tr(S.ariaPanel)}
      >
        {onToggleCollapse ? (
          <IconButton label={tr(S.expand)} onClick={onToggleCollapse}>
            <PanelRightOpen size={18} aria-hidden="true" />
          </IconButton>
        ) : null}
        <span className="lw-agent__rail-glyph" aria-hidden="true">
          <Sparkles size={18} />
        </span>
      </aside>
    );
  }

  const disabled = Boolean(disabledReason);
  const canConfirmPending = Boolean(pendingConfirmation && onConfirmPending && !running && !disabled);
  const canSend = pendingConfirmation ? canConfirmPending : input.trim().length > 0 && !running && !disabled;
  const showResultActions =
    state.status === "done" &&
    hasTurns &&
    Boolean(resultChangedSceneIds?.length) &&
    Boolean(onOpenPreview || onOpenCanvas || onOpenEditor);

  return (
    <aside className="lw-agent" data-collapsed="false" aria-label={tr(S.ariaPanel)}>
      <header className="lw-agent__header">
        <span className="lw-agent__title">
          <Sparkles size={16} className="lw-agent__title-icon" aria-hidden="true" />
          <span className="lw-agent__title-text">{tr(S.title)}</span>
        </span>
        {onToggleCollapse ? (
          <IconButton label={tr(S.collapse)} size="sm" onClick={onToggleCollapse}>
            <PanelRightClose size={16} aria-hidden="true" />
          </IconButton>
        ) : null}
      </header>

      <ScrollArea ref={scrollRef} className="lw-agent__scroll" viewportClassName="lw-agent__viewport">
        <div className="lw-agent__thread">
          {!hasTurns && !pendingConfirmation ? (
            <EmptyState
              icon={<Sparkles size={22} aria-hidden="true" />}
              title={tr(S.emptyTitle)}
              description={tr(S.emptyDesc)}
            />
          ) : (
            state.turns.map((turn, index) => {
              const isLast = index === state.turns.length - 1;
              return (
                <Bubble
                  key={index}
                  turn={turn}
                  streaming={isLast && turn.role === "agent" && running}
                />
              );
            })
          )}

          {state.tools.length > 0 ? (
            <div className="lw-agent__tools" role="list" aria-label={tr(S.toolsLabel)}>
              {state.tools.map((tool) => (
                <div role="listitem" key={tool.id}>
                  <ToolChip tool={tool} />
                </div>
              ))}
            </div>
          ) : null}

          {state.quality ? <QualityCard quality={state.quality} /> : null}

          {showResultActions ? (
            <ResultActions
              changedSceneIds={resultChangedSceneIds ?? []}
              validationChecked={resultValidationChecked}
              validationIssues={resultValidationIssues}
              onOpenPreview={onOpenPreview}
              onOpenCanvas={onOpenCanvas}
              onOpenEditor={onOpenEditor}
            />
          ) : null}

          {undoAvailable && onUndo ? (
            <div className="lw-agent__undo" role="note">
              <div className="lw-agent__undo-copy">
                <span className="lw-agent__undo-title">{tr(S.undoTitle)}</span>
                <span className="lw-agent__undo-body">{tr(S.undoBody)}</span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                loading={undoBusy}
                leadingIcon={<Undo2 size={14} aria-hidden="true" />}
                onClick={onUndo}
              >
                {undoBusy ? tr(S.undoing) : tr(S.undoAction)}
              </Button>
            </div>
          ) : null}

          {state.error ? (
            <div className="lw-agent__error" role="alert">
              <AlertTriangle size={15} className="lw-agent__error-icon" aria-hidden="true" />
              <span className="lw-agent__error-text" title={state.error}>
                {friendlyError(state.error, tr)}
              </span>
            </div>
          ) : null}

          {pendingConfirmation && onConfirmPending && onRevisePending ? (
            <AgentConfirmationCard
              brief={pendingConfirmation}
              compact
              onConfirm={onConfirmPending}
              onRevise={onRevisePending}
            />
          ) : null}
        </div>
      </ScrollArea>

      {running ? (
        <div className="lw-agent__working" role="status" aria-live="polite">
          <Loader2 size={18} className="lw-agent__working-spin" aria-hidden="true" />
          <span className="lw-agent__working-labels">
            <span className="lw-agent__working-title">{activityLabel(state, tr)}</span>
            <span className="lw-agent__working-sub">{tr(S.workingSub)}</span>
          </span>
        </div>
      ) : null}

      {contextChips.length > 0 && !pendingConfirmation ? (
        <div className="lw-agent__context" aria-label={tr(S.contextLabel)}>
          <span className="lw-agent__context-label">{tr(S.contextLabel)}</span>
          <div className="lw-agent__context-chips">
            {contextChips.map((chip) => (
              <span key={`${chip.label}:${chip.value}`} className="lw-agent__context-chip" data-tone={chip.tone ?? "neutral"}>
                <span className="lw-agent__context-chip-label">{chip.label}</span>
                <span className="lw-agent__context-chip-value">{chip.value}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <form
        className="lw-agent__composer"
        data-pending={pendingConfirmation ? "true" : undefined}
        onSubmit={(event) => {
          event.preventDefault();
          if (pendingConfirmation) {
            if (canConfirmPending) onConfirmPending?.();
            return;
          }
          if (canSend) onSend();
        }}
      >
        <Textarea
          className="lw-agent__input"
          aria-label={tr(S.composerLabel)}
          placeholder={pendingConfirmation ? tr(S.pendingPlaceholder) : tr(S.placeholder)}
          rows={pendingConfirmation ? 1 : 2}
          value={input}
          onChange={(event) => onInput(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {disabledReason ? <p className="lw-agent__composer-note">{disabledReason}</p> : null}
        {pendingConfirmation && !disabledReason ? <p className="lw-agent__composer-note">{tr(S.pendingNote)}</p> : null}
        <div className="lw-agent__composer-actions">
          <Button
            type="submit"
            size="sm"
            disabled={!canSend}
            loading={running}
            trailingIcon={pendingConfirmation ? <Check size={14} aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
          >
            {pendingConfirmation ? tr(S.confirmContinue) : tr(S.send)}
          </Button>
        </div>
      </form>
    </aside>
  );
}
